import React, { useEffect, useState, useCallback } from 'react';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiSmartphone, FiBell, FiRefreshCw } from 'react-icons/fi';
import { BACKEND_URL } from '../../config';

// Publish a new app build: every phone shows "New version available — install"
// (popup + banner on Home) and gets a push notification with the ringtone.
const cmp = (a, b) => {
  const pa = `${a || 0}`.split('.').map(Number), pb = `${b || 0}`.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  return 0;
};

export default function SuperAdminAppVersion() {
  const [form, setForm] = useState({ latestVersion: '', releaseNotes: '', iosUrl: '', forceUpdate: false, notify: true });
  const [current, setCurrent] = useState(null);
  const [installs, setInstalls] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, i] = await Promise.all([superAdminApi.get('/app-version'), superAdminApi.get('/app-version/installs')]);
      const ver = v.data.version || {};
      setCurrent(ver);
      setForm(f => ({ ...f, latestVersion: ver.latestVersion || '', releaseNotes: ver.releaseNotes || '', iosUrl: ver.iosUrl || '', forceUpdate: !!ver.forceUpdate }));
      setInstalls(i.data.installs || []);
    } catch { toast.error('Failed to load app version'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (!/^\d+\.\d+\.\d+$/.test(form.latestVersion.trim())) return toast.error('Version like 1.9.4');
    setSaving(true);
    try {
      const r = await superAdminApi.put('/app-version', {
        latestVersion: form.latestVersion.trim(),
        releaseNotes: form.releaseNotes,
        forceUpdate: form.forceUpdate,
        apkUrl: `${BACKEND_URL}/download/app-latest.apk`,
        iosUrl: form.iosUrl,
        notify: form.notify,
      });
      toast.success(form.notify ? `Published — notification sent to ${r.data.notified || 0} phone(s)` : 'Published');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Publish failed'); }
    setSaving(false);
  };

  const latest = current?.latestVersion;
  const upToDate = installs.filter(u => u.appVersion && cmp(u.appVersion, latest) >= 0).length;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title"><FiSmartphone /> Publish new app version</h3></div>
        <div className="card-body">
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
            Live now: <strong>v{latest || '—'}</strong>{current?.forceUpdate ? ' (force update on)' : ''}
            {' · '}APK link: <a href={`${BACKEND_URL}/download/app-latest.apk`} target="_blank" rel="noreferrer">app-latest.apk</a>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">New version <span className="required">*</span></label>
              <input className="form-control" placeholder="e.g. 1.9.4" value={form.latestVersion}
                onChange={e => setForm({ ...form, latestVersion: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">&nbsp;</label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={form.forceUpdate} onChange={e => setForm({ ...form, forceUpdate: e.target.checked })} />
                Force update (app can't be used until updated)
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 6 }}>
                <input type="checkbox" checked={form.notify} onChange={e => setForm({ ...form, notify: e.target.checked })} />
                <FiBell /> Send notification with ringtone to every phone
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">What's new (shown in the update popup)</label>
            <textarea className="form-control" rows={3} placeholder="e.g. Faster printing, cutter notifications"
              value={form.releaseNotes} onChange={e => setForm({ ...form, releaseNotes: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">iPhone link (App Store / TestFlight, optional)</label>
            <input className="form-control" placeholder="https://apps.apple.com/app/…  — leave empty if there is no iPhone build"
              value={form.iosUrl} onChange={e => setForm({ ...form, iosUrl: e.target.value })} />
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            Upload the new APK to the server first (same link), then publish here.
          </div>
          <button className="btn btn-accent" onClick={publish} disabled={saving}>{saving ? 'Publishing…' : 'Publish version'}</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Installed on phones {latest ? `· ${upToDate}/${installs.length} on v${latest}` : ''}</h3>
          <button className="btn btn-outline btn-sm" onClick={load}><FiRefreshCw size={13} /> Refresh</button>
        </div>
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Login</th><th>Shop</th><th>Role</th><th>App version</th><th>Notifications</th><th>Last seen</th></tr></thead>
              <tbody>
                {installs.map(u => {
                  const old = u.appVersion && latest && cmp(u.appVersion, latest) < 0;
                  return (
                    <tr key={u._id}>
                      <td><div style={{ fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{u.email}</div></td>
                      <td>{u.shop}</td>
                      <td><span className="badge badge-gray">{u.role}</span></td>
                      <td>{u.appVersion
                        ? <span className={`badge ${old ? 'badge-danger' : 'badge-success'}`}>v{u.appVersion}{old ? ' · old' : ''}</span>
                        : <span style={{ color: '#94a3b8', fontSize: 12 }}>older than 1.10</span>}</td>
                      <td>{u.notifications ? <span className="badge badge-success">On</span> : <span className="badge badge-gray">Off</span>}</td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
